import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BellRing, X } from 'lucide-react';
import {
  pushSupport, notificationPermission, currentSubscription,
  enablePush, declinePushPrompt, snoozePushPrompt, isPushPromptSnoozed,
} from '@/lib/push';
import { useAuthStore } from '@/store/auth';
import { toast } from '@/store/toast';
import styles from './PushPermissionPrompt.module.css';

/**
 * The one surface that asks an installed driver to turn on notifications.
 *
 * ── Why this exists at all ──────────────────────────────────────────
 * iOS grants web push ONLY to a Home-Screen-installed app, and only
 * from a user gesture. With no surface that asks after install, iOS
 * adoption is approximately zero no matter how good the transport is —
 * a driver would have to go find the toggle in Settings on their own.
 *
 * ── What it deliberately is NOT ─────────────────────────────────────
 * Not an install prompt. Asking a browser-tab user to install the app
 * is a first-run product decision with its own copy, timing and
 * placement questions; it deserves its own thinking rather than riding
 * a transport build. This only appears once the driver has ALREADY
 * installed, which is exactly when the OS will honour the request.
 *
 * ── The three answers, and why they are three ───────────────────────
 *   Enable   → the OS sheet opens (this click IS the gesture).
 *   Not now  → sessionStorage. Asks again NEXT app open, not this one.
 *              The weakest possible "no", because that is what closing
 *              a sheet means.
 *   Don't ask → server-side push_prompt_dismissed_at. Permanent, and
 *              cross-device: browser permission state does not sync, so
 *              a decline on the phone must not re-ask on the tablet.
 *              Reversible only from Settings, deliberately.
 */
export function PushPermissionPrompt() {
  const { t } = useTranslation('settings');
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      // Every gate that means "do not ask", cheapest first.
      if (!isAuthenticated || !user) return;
      // The driver already told us no, on some device, at some point.
      if (user.push_prompt_dismissed_at) return;
      if (isPushPromptSnoozed()) return;
      // Only ask where the ask can succeed: 'needs-install' is an iOS
      // Safari TAB, where requesting permission is impossible and the
      // honest next step is installing — which is not this component's
      // job (see the docblock).
      if (pushSupport() !== 'ready') return;
      // Already answered at the OS level: granted or denied, either way
      // there is nothing to ask.
      if (notificationPermission() !== 'default') return;
      // Registered on another tab already.
      if (await currentSubscription()) return;
      // A short delay so this never lands on top of the first paint or
      // races the guided tour's own auto-open.
      window.setTimeout(() => { if (alive) setVisible(true); }, 2500);
    })();
    return () => { alive = false; };
  }, [isAuthenticated, user]);

  if (!visible) return null;

  const onEnable = async () => {
    setBusy(true);
    try {
      const result = await enablePush();
      setVisible(false);
      if (result === 'enabled') toast(t('push.enabled'), { tone: 'accent' });
      else if (result === 'denied') toast(t('push.deniedToast'), { tone: 'warn' });
      else if (result === 'server-error') toast(t('push.error'), { tone: 'warn' });
    } finally {
      setBusy(false);
    }
  };

  const onNotNow = () => {
    snoozePushPrompt();
    setVisible(false);
  };

  const onNever = () => {
    void declinePushPrompt();
    // Optimistic local sync so the prompt cannot reappear this session
    // even before /auth/me next reconciles.
    // PublicUser coerces this field to a Date, so patch a Date — an ISO
    // string would typecheck as `any` through the store and then read
    // back as the wrong shape.
    useAuthStore.getState().updateUser({ push_prompt_dismissed_at: new Date() });
    setVisible(false);
  };

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="push-prompt-title">
      <div className={styles.sheet}>
        <button type="button" className={styles.close} onClick={onNotNow} aria-label={t('push.notNow')}>
          <X size={18} />
        </button>
        <div className={styles.icon}><BellRing size={26} /></div>
        <h2 id="push-prompt-title" className={styles.title}>{t('push.promptTitle')}</h2>
        <p className={styles.body}>{t('push.promptBody')}</p>
        <button
          type="button"
          className={styles.primary}
          disabled={busy}
          onClick={() => { void onEnable(); }}
        >
          {t('push.enableCta')}
        </button>
        <button type="button" className={styles.secondary} onClick={onNotNow} disabled={busy}>
          {t('push.notNow')}
        </button>
        <button type="button" className={styles.tertiary} onClick={onNever} disabled={busy}>
          {t('push.never')}
        </button>
      </div>
    </div>
  );
}
